import React, { forwardRef, InputHTMLAttributes, useState } from 'react';
import styled from 'styled-components';
import KubitTooltip from '../../KubitTooltip';
import { SvgIcon } from '../../Icon';
import InputField from '../InputField';

interface IProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> {
  value: number | null;
  onChange: (value: number | null) => void;
  error?: string;
}

const Container = styled.div`
  position: relative;
`;

const ErrorIcon = styled(SvgIcon)`
  position: absolute;
  top: 8px;
  right: 6px;
`;

const NumberInput = forwardRef<HTMLInputElement, IProps>(
  ({ error, value, onChange, onBlur, className, ...props }, ref) => {
    const [draftValue, setDraftValue] = useState<string>(
      typeof value === 'number' ? value.toString() : '',
    );
    const handleChange = (newValue: string) => {
      if (!newValue) {
        setDraftValue('');
        onChange(null);
        return;
      }

      // 15 characters limit
      if (newValue.length > 15) {
        return;
      }

      // A special RegExp to check int and float values with
      // optional leading minus sign
      if (/^-?\d*\.?\d*$/.test(newValue)) {
        setDraftValue(newValue);

        const floatValue = parseFloat(newValue);

        if (!isNaN(floatValue)) {
          onChange(floatValue);
        }
      }
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      // Prettify input value onBlur
      setDraftValue((prevState) => {
        if (prevState) {
          return parseFloat(prevState).toString();
        }
        return prevState;
      });

      onBlur?.(event);
    };

    return (
      <Container className={className}>
        <InputField
          ref={ref}
          {...props}
          value={draftValue}
          onChange={handleChange}
          onBlur={handleBlur}
          hasError={Boolean(error)}
        />
        {error && (
          <KubitTooltip content={error} noOpenDelay={true}>
            <ErrorIcon name="error-icon" width={12} height={12} />
          </KubitTooltip>
        )}
      </Container>
    );
  },
);

export default NumberInput;
